import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { ShieldCheck, KeyRound, AtSign, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAdminAuth } from "@/context/AdminAuthContext";

const fmt = (d) => {
  if (d == null) return "Error";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
};

export default function AdminLogin() {
  const auth = useAdminAuth();
  const [email, setEmail] = useState("admin@ovamx.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (auth?.admin && typeof auth.admin === "object") {
    return <Navigate to="/admin" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await auth.login(email, password);
      toast.success("Bienvenido al panel Ovamx");
    } catch (ex) {
      const msg = fmt(ex?.response?.data?.detail) || ex.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen bg-cream flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-between">
          <Logo size={40} />
          <Link to="/" className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid hover:text-coral transition link-underline">
            Volver al sitio
          </Link>
        </div>

        <div className="border hairline bg-cream-card rounded-2xl p-8 shadow-xl">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral inline-flex items-center gap-2">
            <ShieldCheck className="w-3 h-3" /> Panel de administración
          </p>
          <h1 className="mt-3 font-serif text-4xl tracking-tighter leading-tight">
            Acceso<br /><span className="italic text-coral">restringido</span>.
          </h1>
          <p className="mt-3 text-sm text-ink-mid">
            Solo personal autorizado de Ovamx. Tus intentos quedan registrados.
          </p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <Field icon={AtSign} label="Email" testid="admin-email">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                className="w-full bg-cream border hairline rounded-xl pl-10 pr-4 py-2.5 text-sm outline-none focus:border-coral"
              />
            </Field>
            <Field icon={KeyRound} label="Contraseña" testid="admin-password">
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="w-full bg-cream border hairline rounded-xl pl-10 pr-4 py-2.5 text-sm outline-none focus:border-coral"
              />
            </Field>

            {err && (
              <p data-testid="admin-error" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {err}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-ink text-cream rounded-full py-3 text-sm hover:bg-coral transition disabled:opacity-50"
            >
              {loading ? "Verificando…" : "Entrar al panel"}
              <ArrowRight className="w-4 h-4" />
            </button>

            <p className="text-[11px] text-ink-mid font-mono uppercase tracking-[0.2em] text-center pt-2 border-t hairline">
              JWT 24h · bcrypt · 5 intentos = bloqueo
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">{label}</span>
      <span className="relative block">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-mid" />
        {children}
      </span>
    </label>
  );
}
